import api from "@/shared/api/api";
import {
  type ReceivableShareConfig,
  type PaginatedReceivableShareConfigsResponse,
  type GetReceivableShareConfigsParams,
  type CreateReceivableShareConfigPayload,
  type UpdateReceivableShareConfigPayload,
} from "../utils/types";

const PERCENT_FIELDS = [
  "trafficPercentage",
  "trafficMobileClicksPercentage",
  "trafficWebClicksPercentage",
  "trafficConversionClicksPercentage",
  "averageTrafficPercentage",
  "averageTrafficMobileClicksPercentage",
  "averageTrafficWebClicksPercentage",
  "averageTrafficConversionClicksPercentage",
  "presetUsePercentage",
] as const;

const toDecimal = (value?: number | null) =>
  typeof value === "number" && !Number.isNaN(value) ? value / 100 : value;

const toPercent = (value?: number | null) =>
  typeof value === "number" && !Number.isNaN(value) ? value * 100 : value ?? 0;

const mapPayloadToApi = (payload: Partial<CreateReceivableShareConfigPayload>) => {
  const mapped: Record<string, unknown> = { ...payload };

  PERCENT_FIELDS.forEach((field) => {
    if (typeof mapped[field] === "number") {
      mapped[field] = toDecimal(mapped[field] as number);
    }
  });

  return mapped;
};

const mapPercentFieldsFromApi = (shareConfig: ReceivableShareConfig): ReceivableShareConfig => {
  const updated = { ...shareConfig };

  PERCENT_FIELDS.forEach((field) => {
    updated[field] = toPercent(updated[field]) as ReceivableShareConfig[typeof field];
  });

  return updated;
};

const mapPaginatedFromApi = (
  response: PaginatedReceivableShareConfigsResponse,
): PaginatedReceivableShareConfigsResponse => ({
  ...response,
  data: response.data.map(mapPercentFieldsFromApi),
});

export const receivableShareConfigApi = {
  list: async (
    params?: GetReceivableShareConfigsParams,
    signal?: AbortSignal,
  ): Promise<PaginatedReceivableShareConfigsResponse> => {
    const mapped: Record<string, unknown> = {};
    if (params?.page) mapped.page = params.page;
    if (params?.limit) mapped.limit = params.limit;
    if (params?.search) mapped.search = params.search;

    const response = await api<PaginatedReceivableShareConfigsResponse>({
      url: "/v1/receivable-share-configs",
      method: "GET",
      params: mapped,
      signal,
    });

    return mapPaginatedFromApi(response);
  },

  get: async (id: string, signal?: AbortSignal): Promise<ReceivableShareConfig> => {
    const response = await api<ReceivableShareConfig>({
      url: `/v1/receivable-share-configs/${id}`,
      method: "GET",
      signal,
    });
    return mapPercentFieldsFromApi(response);
  },

  create: async (payload: CreateReceivableShareConfigPayload): Promise<ReceivableShareConfig> => {
    const response = await api<ReceivableShareConfig>({
      url: "/v1/receivable-share-configs",
      method: "POST",
      data: mapPayloadToApi(payload),
    });

    return mapPercentFieldsFromApi(response);
  },

  update: async (id: string, payload: UpdateReceivableShareConfigPayload): Promise<ReceivableShareConfig> => {
    const response = await api<ReceivableShareConfig>({
      url: `/v1/receivable-share-configs/${id}`,
      method: "PUT",
      data: mapPayloadToApi(payload),
    });

    return mapPercentFieldsFromApi(response);
  },

  remove: async (id: string): Promise<{ id: string; deletedAt: string }> => {
    return api({
      url: `/v1/receivable-share-configs/${id}`,
      method: "DELETE",
    });
  },
};
